/**
 * Доля хвоста публичного рендерера: вид `select_dropdown` (выпадающий список).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'select_dropdown'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderSelectDropdown(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
// WEB-1694: оформление текста ВНУТРИ поля ввода отдельно от подписи над ним
// (порядок силы слоёв — в inputFieldStyle.ts).
import {
  inputLabelStyleOverride,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  // Слово владельца 21.10.2026: вид самого поля (фон, рамка, скругление,
  // высота, отступ, тень, подсказка, рамка в фокусе).
  inputBoxStyleOverride,
  inputLookClassName,
  inputPlaceholderColorOverride,
} from '../inputFieldStyle';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

export function renderSelectDropdown(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, interp, contentTextColor, values, setValue } = ctx;
  return (
    <div style={{ marginBottom: 16 }}>
      {block.content && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--color-text-primary, inherit)',
            // WEB-1694: свой цвет и кегль ПОДПИСИ — накладкой ПОСЛЕ
            // строки выше. Пустые поля спредят {}: вид прежний до байта.
            ...inputLabelStyleOverride(block),
          }}
        >
          {interp(block.content)}
        </label>
      )}
      <select
        className={inputLookClassName(block)}
        required={block.required}
        value={values[block.id] ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setValue(block.id, e.target.value)
        }
        style={{
          width: '100%',
          padding: '10px 14px',
          border: '2px solid var(--color-border-primary, #d9d9d9)',
          borderRadius: 'var(--border-radius-md, 8px)',
          fontSize: 16,
          background: 'var(--color-bg-surface, #fff)',
          // WEB-775: без color текст выбранной опции наследовал белый под
          // тёмной темой («Fire Red») → white-on-white. Мирроринг
          // date_picker/number_input (WEB-688/689).
          // WEB-1702 п.2: блочный цвет сильнее общего цвета воронки.
          color: contentTextColor ?? 'var(--color-text-primary, inherit)',
          // WEB-1694: свой кегль и цвет ТЕКСТА поля — накладками ПОСЛЕ
          // строк выше. Они правят САМ `<select>`, то есть видимое
          // выбранное значение; раскрытый перечень `<option>` рисует
          // операционная система, и большинство браузеров его оформление
          // игнорирует — обещать это в панели нельзя (см.
          // inputFieldStyle.ts). Пустые поля спредят {}.
          //
          // Возврат приёмки 25.08: пока значение не выбрано, `<select>`
          // показывает пустой `<option>` — ПОДСКАЗКУ, и она рисуется
          // цветом самого элемента, поэтому авторская накладка красила её
          // наравне с ответом (розовое «Select…» на скрине приёмки). У
          // остальных шести полей подсказка остаётся приглушённой —
          // правило записано в inputFieldStyle.ts, у поля даты оно
          // исполняется ровно этим же условием (ветка выше: цвет в
          // состоянии подсказки не спредится, кегль спредится). Второго
          // правила не заводим — применяем то же.
          ...inputTextFontSizeOverride(block),
          // Слово владельца 21.10.2026: вид САМОГО поля; пока ответ не
          // выбран и видна подсказка — свой цвет подсказки автора (если
          // задан), иначе прежнее правило выше. Пусто — {}.
          ...inputBoxStyleOverride(block),
          ...(!values[block.id] && block.placeholder
            ? inputPlaceholderColorOverride(block)
            : inputTextColorOverride(block)),
        }}
      >
        {block.placeholder && (
          <option value="">{interp(block.placeholder)}</option>
        )}
        {/* WEB-2923: подпись варианта — через дверь подстановки; значение
            (`value`) — прежний текст: его пишут в ответ и сравнивают правила
            логики, подстановка сделала бы ответ зависимым от прошлого. */}
        {(block.selectOptions ?? []).map((opt, i) => (
          <option key={i} value={opt}>
            {interp(opt)}
          </option>
        ))}
      </select>
    </div>
  );
}
